/**
 * Animation constants for UI components
 *
 * Centralized timing and animation-related constants used across the application.
 * These values are not responsive (don't change with screen size) but are shared
 * across multiple components for consistency.
 */

export const ANIMATION_CONSTANTS = {
  // Duration in milliseconds
  duration: {
    /** Fast animations (e.g., micro-interactions, hovers) */
    fast: 150,
    /** Normal animations (e.g., modal open/close, transitions) */
    normal: 250,
    /** Slow animations (e.g., page transitions, complex animations) */
    slow: 300,
    /** Long animations (e.g., long-running transitions) */
    long: 500,
    /** Extra-long transitions (e.g. the login propositions fade-in). Mirrors
     *  Flutter's AnimatedOpacity(Duration(milliseconds: 1000)) at
     *  lib/presentation/screens/login/login_screen.dart:283-285. */
    extraLong: 1000,
    /** Minimum duration for drag-to-dismiss animations */
    minDragDismiss: 100,
    /** Idle delay before auto-hiding UI controls (e.g., video controls in fullscreen) */
    autoHideDelay: 3000,
    /** Longer auto-hide delay: the latest-live-gift overlay display window before
     *  it fades out (opacity → 0, gift nulled). Mirrors Flutter's
     *  AppConstants.latestLiveGiftOpacityDuration = 5000
     *  (lib/domain/constants/app_constants.dart:31), used at
     *  lib/presentation/screens/chat/video_call_screen.dart:388-399. */
    autoHideDelayLong: 5000,
    /** Watch threshold (seconds) at which the default-gift suggestion is picked.
     *  Mirrors Flutter's DurationConstants.showDefaultGiftDurationSeconds
     *  (lib/domain/constants/duration_constants.dart). */
    SHOW_DEFAULT_GIFT_SECONDS: 15,
  },

  // Rotation angles in degrees for icon orientation
  rotation: {
    /** Default (pointing up) */
    up: 0,
    /** Pointing right */
    right: 90,
    /** Pointing down / flipped */
    down: 180,
    /** Pointing left */
    left: -90,
    /** Strike-through line over a toggled-off media control icon (Flutter
     *  `Transform.rotate(-pi/3)`, video_call_screen.dart:1277/:1311). */
    strikeThrough: -60,
  },

  // Timing functions (CSS cubic-bezier values)
  easing: {
    /** Standard ease out for most animations */
    easeOut: 'cubic-bezier(0.32, 0.72, 0, 1)',
    /** Bounce effect for dialog entrance */
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    /** Linear for progress indicators */
    linear: 'linear',
    /** CSS default ease curve */
    ease: 'ease',
    /** CSS ease-in-out curve */
    easeInOut: 'ease-in-out',
  },
} as const;

export type AnimationConstants = typeof ANIMATION_CONSTANTS;
